/**
 * Les rendez-vous possibles d'une tribu qu'on ouvre (creer.tsx) : de cinq en
 * cinq minutes, au moins la fenêtre du chat plus cinq minutes d'avance, au
 * plus sept jours, et pendant une session de Hoa quand la régie en a réglé.
 * Le serveur vérifie la même chose (index.ts, `POST /api/tribus`).
 */
import type { Planning } from "./ecrans2.js";

export const MINUTE = 60_000;
const SEMAINE = 7 * 24 * 60 * MINUTE;

/* ------------------------------------------------------------------ */
/* Les rendez-vous possibles                                           */
/* ------------------------------------------------------------------ */

export type Fenetre = [number, number];

/**
 * Les plages où le rendez-vous peut tomber. Une minute de marge sur
 * l'avance : le téléphone arrondit à la minute en dessous en l'envoyant.
 */
export function fenetresRdv(maintenant: number, planning: Planning | null, avanceMin = planning?.avanceMin ?? 20): Fenetre[] {
  const tot = maintenant + (avanceMin + 1) * MINUTE;
  const tard = maintenant + SEMAINE - MINUTE;
  const brutes: Fenetre[] =
    !planning || planning.toujours
      ? [[tot, tard]]
      : [planning.enCours, planning.prochaine].filter((s): s is { debut: number; fin: number } => s !== null).map((s) => [s.debut, s.fin - 15 * MINUTE]);
  return brutes.map(([a, b]): Fenetre => [Math.max(a, tot), Math.min(b, tard)]).filter(([a, b]) => a <= b);
}

export function possible(t: number, fenetres: Fenetre[]): boolean {
  const d = new Date(t);
  return d.getSeconds() === 0 && d.getMinutes() % 5 === 0 && fenetres.some(([a, b]) => t >= a && t <= b);
}

export function debutJour(t: number): number {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function aLHeure(jour: number, h: number, m: number): number {
  const d = new Date(jour);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).getTime();
}

/** Les 288 instants d'un jour, de cinq en cinq minutes. */
export function instantsDuJour(jour: number): number[] {
  const out: number[] = [];
  for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 5) out.push(aLHeure(jour, h, m));
  return out;
}

export function jourPossible(jour: number, fenetres: Fenetre[]): boolean {
  const fin = aLHeure(jour, 23, 55);
  return fenetres.some(([a, b]) => a <= fin && b >= jour) && instantsDuJour(jour).some((t) => possible(t, fenetres));
}

export function plusProche(t: number, candidats: number[], fenetres: Fenetre[]): number | null {
  let meilleur: number | null = null;
  for (const c of candidats) {
    if (!possible(c, fenetres)) continue;
    if (meilleur === null || Math.abs(c - t) < Math.abs(meilleur - t)) meilleur = c;
  }
  return meilleur;
}

/** Tous les instants possibles, du plus tôt au plus tard (sept jours au plus). */
export function tousLesInstants(fenetres: Fenetre[]): number[] {
  const out: number[] = [];
  for (const [a, b] of fenetres) {
    for (let j = debutJour(a); j <= b; j = debutJour(j + 36 * 3_600_000)) {
      for (const t of instantsDuJour(j)) if (t >= a && t <= b && possible(t, fenetres)) out.push(t);
    }
  }
  return out.sort((x, y) => x - y);
}

/**
 * Le rendez-vous proposé d'emblée : la deuxième demi-heure possible, comme
 * avant (laisser à d'autres le temps de voir la tribu), sinon la première.
 */
export function rdvParDefaut(fenetres: Fenetre[]): number | null {
  const tous = tousLesInstants(fenetres);
  const demies = tous.filter((t) => new Date(t).getMinutes() % 30 === 0);
  return demies[1] ?? demies[0] ?? tous[0] ?? null;
}

